import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import Modal from './common/Modal';

const BASE = process.env.REACT_APP_API_BASE_URL;

function formatTime(ts) {
    if (!ts) return '';
    const d = new Date(ts.replace(' ', 'T'));
    const now = new Date();
    if (d.toDateString() === now.toDateString())
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function Avatar({ name, size = 'md' }) {
    const initial = (name || '?')[0].toUpperCase();
    const sz = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
    return (
        <div className={`${sz} rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-white font-bold shrink-0`}>
            {initial}
        </div>
    );
}

function ConvItem({ conv, active, onClick }) {
    const displayName = conv.other_role === 'parent' && conv.child_name
        ? `${conv.other_name} (parent de ${conv.child_name})`
        : conv.other_name;
    const isClosed = conv.status === 'closed';
    const subject = conv.title && conv.title !== conv.other_name ? conv.title : null;
    return (
        <button
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${active ? 'bg-cyan-50 dark:bg-cyan-900/20 border-l-2 border-cyan-500' : ''} ${isClosed ? 'opacity-60' : ''}`}>
            <Avatar name={conv.other_name} size="md" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {subject || displayName}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {isClosed && (
                            <span className="text-[9px] font-semibold text-rose-500 bg-rose-50 dark:bg-rose-900/20 px-1.5 py-0.5 rounded-full">
                                Terminée
                            </span>
                        )}
                        <span className="text-[10px] text-gray-400">{formatTime(conv.last_message_at)}</span>
                    </div>
                </div>
                {subject && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{displayName}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {conv.last_message || 'Pas encore de message'}
                </p>
            </div>
            {!isClosed && parseInt(conv.unread_count || 0) > 0 && (
                <span className="w-5 h-5 rounded-full bg-cyan-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                    {conv.unread_count}
                </span>
            )}
        </button>
    );
}

export default function Messagerie({ currentUserId, userRole }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [tab, setTab]                     = useState('mine');
    const [conversations, setConversations] = useState([]);
    const [children, setChildren]           = useState([]);
    const [activeConvId, setActiveConvId]   = useState(null);
    const [messages, setMessages]           = useState([]);
    const [activeOther, setActiveOther]     = useState(null);
    const [readOnly, setReadOnly]           = useState(false);
    const [isClosed, setIsClosed]           = useState(false);
    const [draft, setDraft]                 = useState('');
    const [sending, setSending]             = useState(false);
    const [loading, setLoading]             = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const [confirmClose, setConfirmClose]   = useState(false);
    const [closing, setClosing]             = useState(false);

    // Modal nouveau message
    const [showNewModal, setShowNewModal]   = useState(false);
    const [userSearch, setUserSearch]       = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser]   = useState(null);
    const [newSubject, setNewSubject]       = useState('');
    const [creating, setCreating]           = useState(false);

    const bottomRef      = useRef(null);
    const activeConvRef  = useRef(null); // ref stable pour le polling
    const lastMsgIdRef   = useRef(0);
    const isParent = userRole === 'parent';

    const loadConversations = useCallback(async () => {
        try {
            const res  = await apiClient(`${BASE}/messages.php?action=conversations`);
            const json = await res.json();
            if (json.success) setConversations(json.conversations);
        } finally { setLoading(false); }
    }, []);

    const loadChildren = useCallback(async () => {
        if (!isParent) return;
        const res  = await apiClient(`${BASE}/messages.php?action=children_conversations`);
        const json = await res.json();
        if (json.success) setChildren(json.children);
    }, [isParent]);

    useEffect(() => {
        loadConversations();
        loadChildren();
    }, [loadConversations, loadChildren]);

    // ?to=userId — ouvrir ou créer une conversation
    useEffect(() => {
        const toId = searchParams.get('to');
        if (!toId) return;
        apiClient(`${BASE}/messages.php?action=create_or_get&with=${toId}`)
            .then(r => r.json())
            .then(j => {
                if (j.success) {
                    setSearchParams({}, { replace: true });
                    loadConversations().then(() => openConversation(j.conversation_id, false));
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ?conv=convId — ouvrir directement une conversation par son ID
    useEffect(() => {
        const convId = searchParams.get('conv');
        if (!convId) return;
        setSearchParams({}, { replace: true });
        loadConversations().then(() => openConversation(parseInt(convId), false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Polling thread actif (3s) + liste conversations (30s)
    useEffect(() => {
        const pollThread = async () => {
            const convId = activeConvRef.current;
            if (!convId || document.hidden) return;
            try {
                const res  = await apiClient(`${BASE}/messages.php?action=thread&id=${convId}&since_id=${lastMsgIdRef.current}`);
                const json = await res.json();
                if (json.success && json.messages.length > 0) {
                    setMessages(prev => [...prev, ...json.messages]);
                    // Marquer lu côté bandeau si la conv est ouverte
                    window.dispatchEvent(new CustomEvent('bandeau-refresh'));
                }
            } catch {}
        };

        const pollConvs = async () => {
            if (document.hidden) return;
            try {
                const res  = await apiClient(`${BASE}/messages.php?action=conversations`);
                const json = await res.json();
                if (json.success) {
                    setConversations(prev => {
                        // Garder unread_count à 0 pour la conv active (déjà lue)
                        return json.conversations.map(c =>
                            c.id === activeConvRef.current ? { ...c, unread_count: 0 } : c
                        );
                    });
                    // Si de nouveaux non-lus dans d'autres convs → rafraîchir bandeau
                    const hasNewUnread = json.conversations.some(c => c.id !== activeConvRef.current && parseInt(c.unread_count) > 0);
                    if (hasNewUnread) window.dispatchEvent(new CustomEvent('bandeau-refresh'));
                }
            } catch {}
        };

        const threadId = setInterval(pollThread, 3000);
        const convsId  = setInterval(pollConvs, 30000);

        // Reprendre immédiatement quand l'onglet redevient visible
        const onVisible = () => { if (!document.hidden) { pollThread(); pollConvs(); } };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(threadId);
            clearInterval(convsId);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Recherche utilisateur (debounce 300ms)
    useEffect(() => {
        if (!showNewModal || selectedUser) return;
        if (userSearch.length < 2) { setSearchResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const res  = await apiClient(`${BASE}/messages.php?action=search_users&q=${encodeURIComponent(userSearch)}`);
                const json = await res.json();
                if (json.success) setSearchResults(json.users);
            } catch {}
        }, 300);
        return () => clearTimeout(t);
    }, [userSearch, showNewModal, selectedUser]);

    const openConversation = useCallback(async (convId, isReadOnly = false) => {
        setActiveConvId(convId);
        lastMsgIdRef.current = 0;
        setReadOnly(isReadOnly);
        setIsClosed(false);
        setConfirmClose(false);
        setLoadingThread(true);
        setMessages([]);
        try {
            const res  = await apiClient(`${BASE}/messages.php?action=thread&id=${convId}`);
            const json = await res.json();
            if (json.success) {
                setMessages(json.messages);
                if (json.read_only !== undefined) setReadOnly(json.read_only);
                if (json.status === 'closed') setIsClosed(true);
            }
        } finally { setLoadingThread(false); }
        if (!isReadOnly) {
            setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
            window.dispatchEvent(new CustomEvent('bandeau-refresh'));
        }
    }, []);

    useEffect(() => {
        const conv = conversations.find(c => c.id === activeConvId);
        if (conv) {
            setActiveOther({
                id: conv.other_id,
                name: conv.other_name,
                role: conv.other_role,
                childId: conv.child_id || null,
                childName: conv.child_name || null,
            });
            if (conv.status === 'closed') setIsClosed(true);
            return;
        }
        for (const child of children) {
            const cc = child.conversations.find(c => c.id === activeConvId);
            if (cc) {
                setActiveOther({ id: cc.other_id, name: cc.other_name, role: cc.other_role, childName: child.child_name });
                return;
            }
        }
        setActiveOther(null);
    }, [activeConvId, conversations, children]);

    // Sync ref pour le polling (évite stale closure)
    useEffect(() => { activeConvRef.current = activeConvId; }, [activeConvId]);

    // Mémoriser le dernier message ID connu
    useEffect(() => {
        if (messages.length > 0) {
            lastMsgIdRef.current = Math.max(...messages.map(m => m.id));
        }
    }, [messages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (e) => {
        e.preventDefault();
        const content = draft.trim();
        if (!content || !activeConvId || readOnly || isClosed) return;
        setSending(true);
        setDraft('');
        try {
            const res  = await apiClient(`${BASE}/messages.php?action=send`, {
                method: 'POST',
                body: JSON.stringify({ conversation_id: activeConvId, content }),
            });
            const json = await res.json();
            if (json.success) {
                setMessages(prev => [...prev, json.message]);
                setConversations(prev => prev.map(c =>
                    c.id === activeConvId
                        ? { ...c, last_message: content, last_message_at: json.message.timestamp }
                        : c
                ));
            }
        } finally { setSending(false); }
    };

    const handleCloseConversation = async () => {
        if (!activeConvId) return;
        setClosing(true);
        try {
            const res  = await apiClient(`${BASE}/messages.php?action=close`, {
                method: 'POST',
                body: JSON.stringify({ conversation_id: activeConvId }),
            });
            const json = await res.json();
            if (json.success) {
                setIsClosed(true);
                setConversations(prev => prev.map(c =>
                    c.id === activeConvId ? { ...c, status: 'closed' } : c
                ));
            }
        } finally {
            setClosing(false);
            setConfirmClose(false);
        }
    };

    const openNewModal = () => {
        setShowNewModal(true);
        setUserSearch('');
        setSearchResults([]);
        setSelectedUser(null);
        setNewSubject('');
    };

    const handleCreateConversation = async () => {
        if (!selectedUser || !newSubject.trim()) return;
        setCreating(true);
        try {
            const res  = await apiClient(`${BASE}/messages.php?action=create`, {
                method: 'POST',
                body: JSON.stringify({ with_id: selectedUser.id, subject: newSubject.trim() }),
            });
            const json = await res.json();
            if (json.success) {
                setShowNewModal(false);
                await loadConversations();
                openConversation(json.conversation_id, false);
            }
        } finally { setCreating(false); }
    };

    const hasMine     = conversations.length > 0;
    const hasChildren = children.some(ch => ch.conversations.length > 0);
    const activeConvSubject = conversations.find(c => c.id === activeConvId)?.title;

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    const newMessageModal = (
        <Modal
            isOpen={showNewModal}
            onClose={() => setShowNewModal(false)}
            title="Nouveau message"
            size="sm"
        >
            <div className="flex flex-col gap-4 -mt-2">
                <div className="relative">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Destinataire</label>
                    <input
                        type="text"
                        value={userSearch}
                        onChange={e => { setUserSearch(e.target.value); setSelectedUser(null); }}
                        placeholder="Rechercher une personne…"
                        autoFocus
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    {selectedUser && (
                        <div className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-500/30">
                            <Avatar name={selectedUser.name} size="sm" />
                            <span className="text-sm font-medium text-cyan-700 dark:text-cyan-300">{selectedUser.name}</span>
                            <span className="text-xs text-cyan-500 capitalize ml-1">{selectedUser.role}</span>
                            <button onClick={() => { setSelectedUser(null); setUserSearch(''); }}
                                className="ml-auto text-cyan-400 hover:text-red-500 transition-colors text-xs">✕</button>
                        </div>
                    )}
                    {searchResults.length > 0 && !selectedUser && (
                        <div className="mt-1 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden shadow-lg">
                            {searchResults.map(u => (
                                <button key={u.id}
                                    onClick={() => { setSelectedUser(u); setUserSearch(u.name); setSearchResults([]); }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors border-b border-gray-100 dark:border-white/5 last:border-0">
                                    <Avatar name={u.name} size="sm" />
                                    <span className="text-sm text-gray-800 dark:text-gray-200">{u.name}</span>
                                    <span className="text-xs text-gray-400 capitalize ml-auto">{u.role}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Sujet</label>
                    <input
                        type="text"
                        value={newSubject}
                        onChange={e => setNewSubject(e.target.value)}
                        placeholder="Ex : Question sur le cours de maths"
                        onKeyDown={e => { if (e.key === 'Enter' && selectedUser && newSubject.trim()) handleCreateConversation(); }}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-white/10 mt-2">
                    <button onClick={() => setShowNewModal(false)}
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                        Annuler
                    </button>
                    <button onClick={handleCreateConversation} disabled={!selectedUser || !newSubject.trim() || creating}
                        className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-medium transition-colors">
                        {creating ? 'Création…' : 'Créer la conversation'}
                    </button>
                </div>
            </div>
        </Modal>
    );

    const leftPanel = (
        <div className={`${activeConvId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 border-r border-gray-200 dark:border-white/10 shrink-0`}>
            {isParent ? (
                <div className="flex border-b border-gray-200 dark:border-white/10">
                    <button onClick={() => setTab('mine')}
                        className={`flex-1 py-3 text-xs font-semibold transition-colors ${tab === 'mine' ? 'text-cyan-600 border-b-2 border-cyan-500' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                        Mes messages
                    </button>
                    <button onClick={() => setTab('children')}
                        className={`flex-1 py-3 text-xs font-semibold transition-colors ${tab === 'children' ? 'text-cyan-600 border-b-2 border-cyan-500' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                        Mes enfants
                    </button>
                </div>
            ) : (
                <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-200 dark:border-white/10">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Messages</h2>
                    <button onClick={openNewModal}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium transition-colors">
                        + Nouveau
                    </button>
                </div>
            )}

            {isParent && tab === 'mine' && (
                <div className="px-4 py-2.5 border-b border-gray-200 dark:border-white/10">
                    <button onClick={openNewModal}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium transition-colors">
                        + Nouveau message
                    </button>
                </div>
            )}

            <div className="flex-1 overflow-y-auto">
                {(!isParent || tab === 'mine') && (
                    <>
                        {!hasMine ? (
                            <div className="flex flex-col items-center justify-center text-sm text-gray-400 text-center px-6 py-12">
                                Aucune conversation.
                                <span className="mt-2 text-xs">Cliquez sur "+ Nouveau" pour en démarrer une.</span>
                            </div>
                        ) : (
                            conversations.map(conv => (
                                <ConvItem key={conv.id} conv={conv} active={activeConvId === conv.id}
                                    onClick={() => openConversation(conv.id, false)} />
                            ))
                        )}
                    </>
                )}

                {isParent && tab === 'children' && (
                    <>
                        {!hasChildren ? (
                            <div className="flex items-center justify-center text-sm text-gray-400 text-center px-6 py-12">
                                Aucune conversation trouvée<br />pour vos enfants.
                            </div>
                        ) : (
                            children.map(child => child.conversations.length > 0 && (
                                <div key={child.child_id}>
                                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-white/5">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{child.child_name}</p>
                                    </div>
                                    {child.conversations.map(conv => (
                                        <ConvItem key={conv.id} conv={{ ...conv, unread_count: 0 }} active={activeConvId === conv.id}
                                            onClick={() => openConversation(conv.id, true)} />
                                    ))}
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const rightPanel = activeConvId ? (
        <div className="flex flex-col flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900">
                <button onClick={() => { setActiveConvId(null); setConfirmClose(false); }}
                    className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500">
                    ←
                </button>
                {activeOther && <Avatar name={activeOther.name} size="sm" />}
                <div className="flex-1 min-w-0">
                    {activeConvSubject && activeConvSubject !== activeOther?.name ? (
                        <>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{activeConvSubject}</p>
                            <p className="text-xs text-gray-400 truncate">
                                {activeOther?.role === 'parent' && activeOther?.childName
                                    ? `${activeOther.name} (parent de ${activeOther.childName})`
                                    : activeOther?.name}
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {activeOther?.role === 'parent' && activeOther?.childName
                                    ? `${activeOther.name} (parent de ${activeOther.childName})`
                                    : activeOther?.name}
                            </p>
                            <p className="text-xs text-gray-400 capitalize">{activeOther?.role}</p>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {['admin','supertuteur','tuteur','professeur'].includes(userRole) && (() => {
                        const studentId = activeOther?.role === 'etudiant'
                            ? activeOther.id
                            : (activeOther?.role === 'parent' ? activeOther.childId : null);
                        if (!studentId) return null;
                        const profilePath = userRole === 'professeur'
                            ? `/professeur/classes?open=${studentId}`
                            : `/admin/eleves?open=${studentId}`;
                        return (
                            <button onClick={() => navigate(profilePath)}
                                className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors font-medium">
                                Voir le profil
                            </button>
                        );
                    })()}

                    {activeOther?.childName && activeOther?.role !== 'parent' && (
                        <span className="text-xs text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-2 py-1 rounded-lg">
                            Conv. de {activeOther.childName}
                        </span>
                    )}

                    {isClosed ? (
                        <span className="text-xs font-semibold text-rose-500 bg-rose-50 dark:bg-rose-900/20 px-2.5 py-1 rounded-lg border border-rose-200 dark:border-rose-500/30">
                            Terminée
                        </span>
                    ) : readOnly ? (
                        <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-lg font-medium">
                            Lecture seule
                        </span>
                    ) : confirmClose ? (
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">Terminer ?</span>
                            <button onClick={handleCloseConversation} disabled={closing}
                                className="text-xs px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white font-medium transition-colors">
                                {closing ? '…' : 'Confirmer'}
                            </button>
                            <button onClick={() => setConfirmClose(false)}
                                className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors font-medium">
                                Annuler
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => setConfirmClose(true)}
                            className="text-xs px-2.5 py-1 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors font-medium">
                            Terminer
                        </button>
                    )}
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-2">
                {loadingThread ? (
                    <div className="text-center text-gray-400 text-sm mt-8">Chargement…</div>
                ) : messages.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm mt-8">
                        {readOnly ? 'Aucun échange encore.' : 'Commencez la conversation.'}
                    </div>
                ) : (
                    messages.map(msg => {
                        const isMe = msg.sender_user_id === currentUserId;
                        return (
                            <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                {!isMe && <Avatar name={msg.sender_name} size="sm" />}
                                <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${isMe ? 'bg-cyan-500 text-white rounded-br-sm' : 'bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200 rounded-bl-sm'}`}>
                                    {!isMe && <p className="text-[10px] font-semibold mb-1 opacity-60">{msg.sender_name}</p>}
                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                    <p className={`text-[10px] mt-1 ${isMe ? 'text-cyan-100' : 'text-gray-400'}`}>{formatTime(msg.timestamp)}</p>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={bottomRef} />
            </div>

            {!readOnly && !isClosed && (
                <form onSubmit={sendMessage} className="px-4 py-3 border-t border-gray-200 dark:border-white/10 flex items-end gap-2">
                    <textarea
                        rows={1}
                        value={draft}
                        onChange={e => {
                            setDraft(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage(e);
                            }
                        }}
                        placeholder="Écrivez un message… (Entrée pour envoyer, Maj+Entrée pour sauter une ligne)"
                        className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none overflow-hidden leading-5" />
                    <button type="submit" disabled={sending || !draft.trim()}
                        className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-medium transition-colors shrink-0">
                        Envoyer
                    </button>
                </form>
            )}

            {readOnly && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-white/10 text-xs text-center text-amber-600 dark:text-amber-400">
                    Vous consultez cette conversation en lecture seule.
                </div>
            )}

            {isClosed && !readOnly && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-white/10 text-xs text-center text-rose-500 dark:text-rose-400">
                    Cette conversation est terminée.
                </div>
            )}
        </div>
    ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-gray-400 text-sm">
            Sélectionnez une conversation
        </div>
    );

    return (
        <>
            {newMessageModal}
            <div className="flex h-[calc(100vh-140px)] bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden shadow-lg">
                {leftPanel}
                {rightPanel}
            </div>
        </>
    );
}
